// components/stats/StatsTable.jsx
import React, { useEffect, useState, useMemo } from "react";
import axios                      from "axios";
import Table                      from "../shared/table/Table";
import { Link }                   from "react-router-dom";
import { FiEdit3, FiTrash2 }      from "react-icons/fi";
import { toast }                  from "react-toastify";
import { confirmAlert }           from "react-confirm-alert";
import useTranslations            from "../../hooks/useTranslations";
import useUserLanguage            from "../../hooks/useUserLanguage";
import "react-confirm-alert/src/react-confirm-alert.css";

axios.defaults.baseURL = process.env.REACT_APP_API_BASE_URL;

export default function StatsTable() {
  /* ───────── STATE ───────── */
  const [rows, setRows] = useState([]);
  const [perm, setPerm] = useState({});
  const lang             = useUserLanguage();
  const { translations } = useTranslations(lang);
  const t = (k, d) => translations[k] || d;

  /* ───────── FETCH ───────── */
  useEffect(() => { fetchRows(); fetchPerm(); }, []);

  const fetchRows = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const { data } = await axios.get("/api/stats/list", {
        headers:{ Authorization:`Bearer ${token}` },
      });
      setRows(data);                    // beklenen veri: [{id, institution:{title}, student_count, test_count, updated_at}, …]
    } catch (e) { console.error(e); }
  };

  const fetchPerm = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const { data } = await axios.get("/api/admin/my-permissions", {
        headers:{ Authorization:`Bearer ${token}` },
      });
      setPerm(data.permissions?.find(p=>p.module_key==="static_admin_accounts")||{});
    } catch { setPerm({}); }
  };

  /* ───────── PERM ───────── */
  const canEdit   = perm.edit_permission   || perm.full_access;
  const canDelete = perm.delete_permission || perm.full_access;

  /* ───────── DELETE ───────── */
  const handleDelete = id => {
    confirmAlert({
      title   : t("DELETE_CONFIRM_TITLE","Silmek istediğinize emin misiniz?"),
      message : t("DELETE_CONFIRM_MSG","Bu kayıt silinecek."),
      buttons : [
        { label:t("YES","Evet"), async onClick(){
            try{
              const token = localStorage.getItem("admin_token");
              await axios.delete(`/api/stats/${id}`, {
                headers:{ Authorization:`Bearer ${token}` }
              });
              toast.success(t("SUCCESS_DELETE","Silindi"));
              fetchRows();
            }catch{ toast.error("Hata"); }
          }},
        { label:t("CANCEL","İptal") }
      ]
    });
  };

  /* ───────── COLUMNS ───────── */
  const columns = useMemo(() => [
    /* KURUM */
  {
    accessorKey : "institution",
    header      : () => t("INSTITUTION","Kurum"),
    meta        : { headerClassName:"text-start institution-column", className:"text-start institution-column" },
    cell        : info => info.getValue()?.title || "-",
  },

  /* DANIŞMAN — sabit */
  {
    accessorKey : "student_count",
    header      : () => t("STUDENT_COUNT","Danışan"),
    meta        : { headerClassName:"text-start col-student", className:"text-start col-student" },
    size        : 120,
  },

    
    /* TEST SAYISI */
    {
      accessorKey : "test_count",
      header      : () => t("TEST_COUNT","Çözülen Test"),
      meta        : { headerClassName:"text-start", className:"text-start" },
      size        : 140,
    },

    /* GÜNCELLENME */
    {
      accessorKey: "updated_at",
      header: () => (
        <div style={{ textAlign:"start", width:"90%" }}>
          {t("TABLE_DATE_LABEL","Tarih")}
        </div>
      ),
      meta : { headerClassName:"text-start", className:"text-start" },
      size : 140,
      cell : info => {
        const d = new Date(info.getValue()).toLocaleDateString("en-GB");
        return <div style={{ textAlign:"start", width:"90%" }}>{d}</div>;
      },
    },

    /* ACTIONS */
    {
      accessorKey: "actions",
      header: () => (
        <div style={{ textAlign:"right", paddingRight:"50px" }}>
          {t("TABLE_ACTIONS_LABEL","İşlemler")}
        </div>
      ),
      enableSorting:false,
      meta:{ headerClassName:"text-end", className:"text-end" },
      size: 180,
      cell: info => {
        const id = info.row.original.id;
        return (
          <div className="hstack gap-2 justify-content-end" style={{ paddingRight:"20px" }}>
            {canEdit && (
              <Link
                to={`/stat/edit/${id}`}
                className="avatar-text avatar-md text-success bg-soft-success border-soft-success"
                title={t("EDIT","Düzenle")}
              >
                <FiEdit3/>
              </Link>
            )}
            {canDelete && (
              <button
                type="button"
                className="avatar-text avatar-md text-danger bg-soft-danger border-soft-danger"
                title={t("DELETE","Sil")}
                onClick={() => handleDelete(id)}
              >
                <FiTrash2/>
              </button>
            )}
          </div>
        );
      },
    },
  ], [t, canEdit, canDelete]);

  /* ───────── RENDER ───────── */
  if (!rows.length) return <p>{t("LOADING","Yükleniyor…")}</p>;
  return <Table data={rows} columns={columns} />;
}
